import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useL } from "@/lib/useL";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DASH } from "@/constants/testIds";
import { Loader2, Copy, Trash2, Upload, Link2, FileText, Save, Sparkles, BookOpen, Lock, Camera, RefreshCw, X, CornerDownLeft, ClipboardCheck, FolderOpen } from "lucide-react";
import { useBilling } from "@/contexts/BillingContext";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { UsageMeter, Paywall } from "@/components/Billing";
import { AnonSignupModal } from "@/components/AnonSignupModal";
import { TimeSavedWidget } from "@/components/TimeSavedWidget";
import { InterfaceTour } from "@/components/InterfaceTour";
import { LastCallBanner } from "@/components/LastCallBanner";
import { PromoUpgradeModal } from "@/components/PromoUpgradeModal";
import { OnboardingCTA } from "@/components/OnboardingCTA";
import FreeCasesStrip from "@/components/FreeCasesStrip";
import { ExtrasDrawer } from "@/components/ExtrasDrawer";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import { notifyOperationComplete, playBell, primeAudio } from "@/lib/soundNotifications";
import { renderSummary } from "@/lib/renderSummary";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { BookScanPanel } from "@/components/BookScanPanel";
import { ShareAndPrint } from "@/components/ShareAndPrint";
import { SpeakButton } from "@/components/SpeakButton";
import { ImagePreview } from "@/components/ImagePreview";
import { BookPromoBanner } from "@/components/BookPromoBanner";
import { FollowUpBox } from "@/components/FollowUpBox";
import { SummaryActions } from "@/components/SummaryActions";
import { BigBookTip } from "@/components/BigBookTip";
import { MetaphorHint } from "@/components/MetaphorHint";
import { ACTION_HELP } from "@/components/actionHelp";
import { ReciteBox } from "@/components/ReciteBox";
import { VerifiedSummary } from "@/components/VerifiedSummary";
import { FlashcardsButton } from "@/components/FlashcardsButton";
import { SummariesCounter } from "@/components/SummariesCounter";
import { MindmapButton } from "@/components/MindmapButton";
import { ReviewPrompt } from "@/components/ReviewPrompt";
import { TripleLevels } from "@/components/TripleLevels";
import { DossierPanel, combineDossier } from "@/components/DossierPanel";
import { PrintQr } from "@/components/PrintQr";
import { readDraft, saveDraft } from "@/lib/draft";
import { shareUrlFor } from "@/lib/shareText";
import { ActionBarButton } from "@/components/ActionBar";
import { detectAiPrompt } from "@/lib/detectAiPrompt";
import OutputConfigPanel from "@/components/OutputConfigPanel";
import { CustomInstructionPanel } from "@/components/CustomInstructionPanel";
import { DiscoveryHeroBanner } from "@/components/DiscoveryHeroBanner";
import { CreditEstimate } from "@/components/CreditEstimate";
import { MonthlyQuotaMeter } from "@/components/MonthlyQuotaMeter";
import { BoosterPaywall } from "@/components/BoosterPaywall";
import { InstantExample } from "@/components/InstantExample";
import { WelcomeLine } from "@/components/WelcomeLine";
import { FORMAT_LABELS } from "@/lib/formatLabels";
import { DashboardBookCard } from "@/components/DashboardBookCard";
import { useAuth } from "@/contexts/AuthContext";
import { trackSummaryGenerated, trackPaywallShown } from "@/lib/analytics";

const TAB_TEXT = "text";
const TAB_FILE = "file";
const TAB_URL = "url";
const TAB_BOOK = "book";
const TAB_DOSSIER = "dossier";

/**
 * Preview of the extracted text (URL / file upload). Shows a truncated view
 * by default (avoids drowning the page in 20k chars from long articles) but
 * makes the truncation clearly labelled AND expandable so users don't panic
 * about "missing content" — the full text is always sent to the summariser.
 */
const ExtractedPreview = ({ text, sourceLabel, previewLimit = 1500 }) => {
  const L = useL();
  const [expanded, setExpanded] = React.useState(false);
  const total = text.length;
  const isTruncated = total > previewLimit;
  const visible = expanded || !isTruncated ? text : text.slice(0, previewLimit);
  return (
    <div className="mt-6 border border-border bg-secondary/30">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="sux-marker">
          {L("Source :", "Source:")} {sourceLabel} · {total.toLocaleString()} {L("caractères extraits", "characters extracted")}
        </div>
        {isTruncated && (
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid="extracted-preview-toggle"
            className="font-mono uppercase tracking-[0.14em] text-[10px] px-2 py-1 border border-border hover:bg-[#002FA7] hover:text-white transition-colors"
          >
            {expanded ? L("Réduire l'aperçu", "Collapse preview") : L("Voir tout", "Show all")}
          </button>
        )}
      </div>
      <div className={`px-4 py-3 overflow-auto text-sm font-['IBM_Plex_Sans'] leading-relaxed ${expanded ? "max-h-[400px]" : "max-h-72"}`}>
        <p className="whitespace-pre-wrap">{visible}</p>
      </div>
      {isTruncated && !expanded && (
        <div
          data-testid="extracted-preview-banner"
          className="px-4 py-2 border-t border-border bg-[#002FA7]/5 text-[12px] text-[#002FA7] font-medium"
        >
          {L(`Aperçu limité aux ${previewLimit.toLocaleString()} premiers caractères. Le texte complet (${total.toLocaleString()} caractères) sera résumé.`, `Preview limited to the first ${previewLimit.toLocaleString()} characters. The full text (${total.toLocaleString()} characters) will be summarised.`)}
        </div>
      )}
    </div>
  );
};

// Text box grows with its content (keeps blank room under the text to keep typing).
const autoGrow = (el) => {
  if (!el) return;
  // Mobile: cap to 60% of the screen so the finger can always scroll the page around it.
  const mobile = window.innerWidth < 768;
  const min = mobile ? 260 : 420;
  const max = mobile ? Math.round(window.innerHeight * 0.6) : 1800;
  el.style.height = "auto";
  el.style.height = `${Math.max(min, Math.min(el.scrollHeight, max))}px`;
};

export default function Dashboard() {
  const { t, i18n } = useTranslation();
  const L = useL();
  const navigate = useNavigate();
  const location = useLocation();
  const [reworkSource, setReworkSource] = useState(null);
  // When a Famille B cas pratique (self_contained) is loaded, the
  // resolved prompt lives in `text` and we ask the backend to execute it
  // literally instead of producing a generic summary. Reset the moment
  // the user edits the textarea manually — once they start typing their
  // own content, intent flips back to "summarize this".
  const [directExecution, setDirectExecution] = useState(false);
  // Prompt-type cas pratique in flight: the teacher scanned a cas card
  // whose "prompt" is a transformation to apply ON TOP of their own
  // content (QCM, mail parents, plan de séance…). We stash it here and
  // forward it to FollowUpBox once the first summary is ready.
  const [pendingFollowUp, setPendingFollowUp] = useState(null);
  // When the user pastes something that looks like a meta-prompt, we show
  // a dismissible warning (see detectAiPrompt.js). This flag records a
  // user-level dismissal so clicking "Résumer quand même" doesn't surface
  // the banner again for the current session until they clear the field.
  const [promptWarningDismissed, setPromptWarningDismissed] = useState(false);
  const fileRef = useRef(null);
  const { isLocked, isMonthlyLocked, credits, refresh: refreshBilling, billing, freeAccess } = useBilling();
  const isPro = !!billing?.is_pro;
  const isAdmin = freeAccess;
  const { user } = useAuth();
  const canChapters = isPro || isAdmin || credits > 0;
  // Booster paywall state — opened manually from MonthlyQuotaMeter, or
  // auto-opened the moment a Pro user exhausts both their monthly quota
  // and their bonus bank.
  const [boosterOpen, setBoosterOpen] = useState(false);
  const [boosterReason, setBoosterReason] = useState("manual");
  useEffect(() => {
    if (isMonthlyLocked) {
      setBoosterReason("quota_hit");
      setBoosterOpen(true);
    }
  }, [isMonthlyLocked]);
  const online = useOnlineStatus();
  const [tab, setTab] = useState(TAB_TEXT);
  const [text, setText] = useState(() => readDraft().text || "");
  const [url, setUrl] = useState("");
  const [dossierDocs, setDossierDocs] = useState([]);
  useEffect(() => {
    if (tab !== TAB_DOSSIER) return;
    setText(dossierDocs.length ? combineDossier(dossierDocs) : "");
    setSourceLabel(dossierDocs.length ? `Dossier : ${dossierDocs.map((d) => d.label).join(" + ")}`.slice(0, 120) : null);
  }, [dossierDocs, tab]);
  const [sourceLabel, setSourceLabel] = useState(null);
  // Local File object of the last imported image (used only for previewing
  // it back to the user via ImagePreview). Reset whenever the source
  // changes (text tab, URL tab, or a new file is picked).
  const [imageFile, setImageFile] = useState(null);
  const [meta, setMeta] = useState({
    models: [],
    lengths: [],
    formats: [],
    languages: [],
    registers: [],
  });
  const [model, setModel] = useState("gemini-3-flash");
  const [length, setLength] = useState("medium");
  const [format, setFormat] = useState(() => {
    const f = new URLSearchParams(window.location.search).get("format");
    return f && FORMAT_LABELS[f] ? f : "both";
  });
  // Optional teacher-authored transformation prompt. When non-empty it
  // replaces the format directive sent to the LLM. Pre-filled when the
  // teacher arrives from a prompt-type cas pratique.
  const [customInstruction, setCustomInstruction] = useState("");
  const [language, setLanguage] = useState("auto");
  const [register, setRegister] = useState(() => {
    try {
      return localStorage.getItem("sux1_register") || "auto";
    } catch (_) {
      return "auto";
    }
  });
  // Teacher-mode audience level — only relevant when format === "enseignant".
  // Defaults to "college" (11–15 y.o.) as it's the modal French classroom target.
  const [audienceLevel, setAudienceLevel] = useState(() => {
    try {
      return localStorage.getItem("sux1_audience_level") || "aucun";
    } catch (_) {
      return "aucun";
    }
  });
  // Fiche encyclopédique entity type — only relevant when format === "biographie".
  // One of: "personne" | "lieu" | "oeuvre" | "concept" | "evenement" | "auto".
  // "auto" lets the LLM detect from the input itself (useful for mixed usage).
  const [ficheType, setFicheType] = useState(() => {
    try {
      return localStorage.getItem("sux1_fiche_type") || "personne";
    } catch (_) {
      return "personne";
    }
  });

  useEffect(() => {
    try { localStorage.setItem("sux1_register", register); } catch (_) {}
  }, [register]);
  useEffect(() => {
    try { localStorage.setItem("sux1_audience_level", audienceLevel); } catch (_) {}
  }, [audienceLevel]);
  useEffect(() => {
    try { localStorage.setItem("sux1_fiche_type", ficheType); } catch (_) {}
  }, [ficheType]);
  // Mobile freeze guard — abort any in-flight NDJSON stream when:
  //   1. The user navigates away (component unmounts).
  //   2. A quota paywall / anon-signup modal takes over (isLocked flips true).
  // Without this, mobile Safari keeps consuming the ReadableStream under the
  // modal, allocating per-chunk strings until the UI thread stalls.
  useEffect(() => {
    return () => {
      if (streamControllerRef.current) {
        try { streamControllerRef.current.abort(); } catch (_) {}
        streamControllerRef.current = null;
      }
    };
  }, []);
  useEffect(() => {
    if (isLocked && streamControllerRef.current) {
      try { streamControllerRef.current.abort(); } catch (_) {}
      streamControllerRef.current = null;
      setBusy(false);
    }
  }, [isLocked]);
  const [busy, setBusy] = useState(false);
  // AbortController ref for the active NDJSON stream (if any). We MUST abort
  // the reader when:
  //   • The component unmounts (user navigates away) — otherwise mobile Safari
  //     keeps consuming chunks in the background and the heap grows unchecked.
  //   • A 402 paywall or anon-signup modal takes over the UI — the stream is
  //     dead-code at that point but was consuming CPU/memory, which froze
  //     low-end mobiles.
  const streamControllerRef = useRef(null);
  const [extracting, setExtracting] = useState(false);
  const [result, setResult] = useState(() => readDraft().result || null);
  const [mode, setMode] = useState(() => readDraft().mode || "single"); // "single" | "chapters"
  const [chapterPreview, setChapterPreview] = useState(null); // { count, used_fallback } | null
  const [chaptersResult, setChaptersResult] = useState(() => readDraft().chaptersResult || null); // ChaptersResponse | null
  // Streaming state for chapter mode: emits partial results as they arrive so
  // the UI does not stay blank for 30+ seconds waiting on 40 LLM calls.
  const [chaptersStream, setChaptersStream] = useState(null); // { count, title, received: [ChapterItem] } | null
  const [chaptersError, setChaptersError] = useState(null); // persistent red banner (survives toast dismissal)
  // Progress state for the global summarize stream (map/reduce phases on huge inputs).
  const [globalProgress, setGlobalProgress] = useState(null); // { phase: 'map'|'reduce'|'starting', done?, total? } | null
  // Persistent red banner for global-mode failures — survives toast auto-dismiss so
  // the user always knows the run failed (rather than seeing an empty result area).
  const [globalError, setGlobalError] = useState(null);
  // Public-share state for the primary Dashboard summary card. Enables the
  // LinkedIn 1-click share (which requires a public URL) directly from the
  // freshly-generated summary, mirroring the behaviour of SummaryDetail.
  const [sharingResult, setSharingResult] = useState(false);
  const resultShareUrl = result
    ? shareUrlFor(result.id)
    : "";
  const onEnsureResultPublic = async () => {
    if (!result || result.is_public) return;
    setSharingResult(true);
    try {
      const { data } = await api.post(`/summaries/${result.id}/share`);
      setResult((cur) => (cur ? { ...cur, is_public: data.is_public } : cur));
    } finally {
      setSharingResult(false);
    }
  };

  useEffect(() => {
    api.get("/models").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  // ---- Rework pipeline (from SummaryDetail → "Retravailler ce texte") ----
  // Reads router state on mount, prefills the dashboard, and immediately
  // clears the state so a page refresh does not resurrect the rework banner.
  useEffect(() => {
    const rw = location.state?.rework;
    if (!rw) return;
    setTab(TAB_TEXT);
    setText(rw.text || "");
    if (rw.model) setModel(rw.model);
    if (rw.length) setLength(rw.length);
    if (rw.format) setFormat(rw.format);
    if (rw.audienceLevel) setAudienceLevel(rw.audienceLevel);
    if (rw.language) setLanguage(rw.language);
    if (rw.register) setRegister(rw.register);
    setReworkSource(rw.sourceTitle || null);
    // Prompt-type cas pratiques: the "prompt" is a transformation applied
    // on top of teacher-provided content. We pre-fill the new upstream
    // `customInstruction` field so the teacher just pastes/scans their
    // content and clicks Résumer once — direct action, 1 quota, no
    // follow-up step needed. `pendingFollowUp` is kept as a secondary
    // option (visible banner) only when the caller explicitly asks for
    // the 2-step flow.
    //
    // Famille B override (Oct 2026 — `rw.directExecution`): the resolved
    // prompt is self-contained, so it was placed in `rw.text` (handled
    // by setText(rw.text) above). We flag it on state so the backend
    // request can tell the LLM "execute literally, don't summarize", and
    // so the Dashboard renders a dedicated banner instead of the Famille A
    // explainer that would ask the user to paste content.
    if (rw.directExecution && rw.text) {
      setCustomInstruction("");
      setPendingFollowUp(null);
      setDirectExecution(true);
    } else if (rw.customInstruction) {
      setCustomInstruction(rw.customInstruction);
      setPendingFollowUp(null);
      setText("");
      setDirectExecution(false);
    } else if (rw.pendingFollowUp) {
      setPendingFollowUp({
        prompt: rw.pendingFollowUp,
        label: rw.sourceTitle || "cas pratique",
      });
      setText("");
      setDirectExecution(false);
    } else {
      setPendingFollowUp(null);
      setDirectExecution(false);
    }
    // Wipe the router state so subsequent refreshes / navigations don't re-apply it.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pick up a "Summarize now" hand-off from the Reading List page (stored in
  // localStorage so we can carry huge text bodies that would blow the URL).
  useEffect(() => {
    let raw = null;
    try { raw = localStorage.getItem("sux1_reading_list_load"); } catch (_) {}
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      if (payload.kind === "url" && payload.url) {
        setTab(TAB_URL);
        setUrl(payload.url);
      } else if (payload.kind === "text" && payload.text) {
        setTab(TAB_TEXT);
        setText(payload.text);
        if (payload.title) setSourceLabel(payload.title);
      }
    } catch (_) {}
    try { localStorage.removeItem("sux1_reading_list_load"); } catch (_) {}
  }, []);

  // Debounced preview when in chapter mode
  useEffect(() => {
    if (mode !== "chapters" || !canChapters) {
      setChapterPreview(null);
      return;
    }
    if (text.trim().length < 200) {
      setChapterPreview(null);
      return;
    }
    const handle = setTimeout(() => {
      api
        .post("/summarize/chapters/preview", { text, model })
        .then(({ data }) => setChapterPreview(data))
        .catch(() => setChapterPreview(null));
    }, 600);
    return () => clearTimeout(handle);
  }, [text, mode, canChapters, model]);

  // Auto-switch to Gemini 3 Flash on Book tab (faster first impression, ~10s
  // instead of ~60s).
  // When the user switches to the Book tab we automatically pull them off
  // Claude Sonnet 4.5 (slower, plafonne à 60 s sur les livres) toward
  // Gemini 3 Flash, which handles 800 k+ char digests in <20 s reduce.
  // Only applies when the user is still on the default Claude choice so
  // we do not override an explicit preference.
  const prevTabRef = useRef(tab);
  useEffect(() => {
    if (prevTabRef.current !== TAB_BOOK && tab === TAB_BOOK && model === "claude-sonnet-4-5") {
      setModel("gemini-3-flash");
    }
    prevTabRef.current = tab;
  }, [tab, model]);

  const charCount = text.length;
  const textAreaRef = useRef(null);
  useLayoutEffect(() => { autoGrow(textAreaRef.current); }, [text, tab]);

  // Keep the work in progress when the mobile browser reloads the tab (app switch).
  useEffect(() => {
    if (busy) return undefined;
    const id = setTimeout(() => saveDraft({ text, result, chaptersResult, mode }), 800);
    return () => clearTimeout(id);
  }, [text, result, chaptersResult, mode, busy]);
  const wordCount = useMemo(() => (text.trim() ? text.trim().split(/\s+/).length : 0), [text]);

  // Cost-optimised long-text policy:
  //   • > 100 k chars: Claude Sonnet 4.5 disabled (Cloudflare 60 s cap AND
  //     10-15× more expensive than Gemini). Default auto-swap to Gemini
  //     3 Flash — fastest AND cheapest for map-reduce.
  //   • > 500 k chars: also lock GPT-5.2 out. Only Gemini 3 Flash allowed,
  //     the only model that reliably finishes a 3M-char book digest under
  //     the ingress cap and stays affordable (~$0.75 vs ~$9 on GPT-5.2 for
  //     1.5M tokens).
  const CLAUDE_LONG_TEXT_LIMIT = 100_000;
  const GPT_LONG_TEXT_LIMIT = 500_000;
  const claudeUnavailable = charCount > CLAUDE_LONG_TEXT_LIMIT;
  const gptUnavailable = charCount > GPT_LONG_TEXT_LIMIT;
  const disabledModels = useMemo(() => {
    const list = [];
    if (claudeUnavailable) list.push("claude-sonnet-4-5");
    if (gptUnavailable) list.push("gpt-5.2");
    return list;
  }, [claudeUnavailable, gptUnavailable]);
  useEffect(() => {
    if (
      (claudeUnavailable && model === "claude-sonnet-4-5") ||
      (gptUnavailable && model === "gpt-5.2")
    ) {
      setModel("gemini-3-flash");
    }
  }, [claudeUnavailable, gptUnavailable, model]);
  const modelNote = gptUnavailable
    ? L("Livre > 500 000 caractères : Gemini 3 Flash recommandé (le plus rapide et le seul qui accepte cette taille)", "Book > 500,000 characters: Gemini 3 Flash recommended (the fastest and the only one that accepts this size)")
    : claudeUnavailable
    ? L("Texte > 100 000 caractères : Gemini 3 Flash recommandé (10× plus rapide et adapté aux longs documents)", "Text > 100,000 characters: Gemini 3 Flash recommended (10× faster and suited to long documents)")
    : null;

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Remember the file only if it's an image, so we can preview it back
    // to the user. Non-image sources (PDF, DOCX, EPUB…) don't need a
    // visual preview — the extracted text is enough.
    setImageFile(file.type?.startsWith("image/") ? file : null);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("language", i18n.resolvedLanguage || "auto");
      const { data } = await api.post("/extract/file", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText(data.text);
      setSourceLabel(data.source_label);
      toast.success(t("dashboard.extracted_chars", { count: data.text.length.toLocaleString(), source: data.source_label }));
      playBell();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dashboard.extract_failed"));
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onExtractUrl = async () => {
    if (!url.trim()) return;
    setExtracting(true);
    setImageFile(null);
    try {
      const { data } = await api.post("/extract/url", { url: url.trim() });
      setText(data.text);
      setSourceLabel(data.source_label);
      toast.success(t("dashboard.extracted_from", { source: data.source_label }));
      playBell();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dashboard.url_failed"));
    } finally {
      setExtracting(false);
    }
  };

  // Auto-clear a very long input once its summary is ready. When someone
  // pastes a full book (millions of chars), keeping that content in the
  // textarea makes every subsequent keystroke re-render megabytes of text
  // — feels frozen on mobile, and there's no realistic "rework" scenario
  // for a whole book anyway. Small/medium inputs are preserved so the
  // "Retravailler" and "Développer" flows keep working.
  const CLEAR_INPUT_AFTER_SUCCESS_THRESHOLD = 100_000;
  const clearHeavyInputIfNeeded = () => {
    if (text.length >= CLEAR_INPUT_AFTER_SUCCESS_THRESHOLD) {
      setText("");
      setSourceLabel(null);
      setImageFile(null);
      toast.info(L("Zone de texte effacée : collez un nouveau texte quand vous voulez.", "Text area cleared: paste a new text whenever you like."));
    }
  };

  const onSummarize = async () => {
    // CRITICAL for mobile audio: prime the SpeechSynthesis + AudioContext
    // right here, while we're still inside the user's tap gesture. Without
    // this, iOS Safari silently refuses to speak "Résumé prêt" 30 s later
    // because the gesture-scoped audio permission has already expired.
    primeAudio();
    if (text.trim().length < 3) {
      toast.error(t("dashboard.min_text_toast"));
      return;
    }
    if (mode === "chapters") {
      if (!canChapters) {
        toast.error(t("dashboard.mode_chapters_pro_only"));
        return;
      }
      if (text.trim().length < 200) {
        toast.error(t("dashboard.min_text_chapters"));
        return;
      }
    }
    // Preemptive warning for very large inputs. The Kubernetes ingress kills
    // requests after ~120s, and even the largest LLM (Gemini 3 Flash, 1M
    // tokens ≈ 4M chars) will time out on huge inputs when running in "global"
    // mode. Recommend chapter mode above 500k chars, and hard-refuse above
    // 3.5M chars (context window ceiling).
    const chars = text.length;
    if (mode === "single" && chars > 500000) {
      const ok = window.confirm(
        L(`Ce texte fait ${chars.toLocaleString()} caractères : c'est très long pour un résumé global.\n\nConseil : passez en mode « Chapitre par chapitre » pour un résultat plus fiable et lisible.\n\nVoulez-vous quand même tenter un résumé global ? (cela peut échouer)`,
          `This text is ${chars.toLocaleString()} characters long: that is very long for a global summary.\n\nTip: switch to « Chapter by chapter » mode for a more reliable, readable result.\n\nDo you still want to try a global summary? (it may fail)`)
      );
      if (!ok) return;
    }
    if (chars > 3500000) {
      toast.error(
        L(`Texte trop volumineux (${chars.toLocaleString()} caractères). Aucun modèle IA ne peut le traiter en une fois. Réduisez le texte ou découpez-le en plusieurs parties.`,
          `Text too large (${chars.toLocaleString()} characters). No AI model can process it in one go. Shorten the text or split it into several parts.`)
      );
      return;
    }
    setBusy(true);
    setResult(null);
    setChaptersResult(null);
    setChaptersStream(null);
    setChaptersError(null);
    setGlobalProgress(null);
    setGlobalError(null);
    try {
      if (mode === "chapters") {
        // Streaming NDJSON: chapters arrive progressively so the UI never
        // blanks out for 30+ seconds and the ingress does not idle-timeout.
        await streamChapters({
          text,
          model,
          length,
          format,
          language,
          register,
          audience_level: audienceLevel !== "aucun" ? audienceLevel : null,
          fiche_type: format === "biographie" ? ficheType : null,
          source_type: tab,
          source_label: sourceLabel,
          custom_instruction: customInstruction?.trim() || null,
        });
      } else {
        // Global summarize — always via the streaming endpoint. Short texts
        // (< 80k chars) return a single "done" event; long texts trigger the
        // two-phase map-reduce flow (chunk digests → final summary) so we can
        // reliably summarise entire books without hitting the 60s ingress cap.
        await streamGlobalSummary({
          text,
          model,
          length,
          format,
          language,
          register,
          audience_level: audienceLevel !== "aucun" ? audienceLevel : null,
          fiche_type: format === "biographie" ? ficheType : null,
          source_type: tab,
          source_label: sourceLabel,
          custom_instruction: customInstruction?.trim() || null,
          direct_execution: directExecution,
          dossier: tab === TAB_DOSSIER && dossierDocs.length > 1,
        });
      }
      refreshBilling();
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.code;
      if (status === 402) {
        refreshBilling();
        trackPaywallShown(mode === "chapters" ? "chapters" : "generate");
        // Pro user hitting the monthly cap triggers the booster paywall
        // (NOT the Pro upgrade paywall). The backend tags this with
        // `monthly_quota_exceeded` so we can distinguish from the free
        // quota paywall.
        const detail = err?.response?.data?.detail || "";
        if (/anon_quota/i.test(detail)) {
          // AnonSignupModal is already shown via isLocked: no extra toast.
        } else if (/free_trial_closed/i.test(detail)) {
          setBoosterReason("trial_closed");
          setBoosterOpen(true);
        } else if (mode === "chapters") {
          toast.error(t("dashboard.mode_chapters_pro_only"));
        } else if (/credits_required|monthly_quota_exceeded/i.test(detail) || isPro) {
          setBoosterReason("quota_hit");
          setBoosterOpen(true);
        } else {
          toast.error(t("billing.credits_required"));
        }
      } else if (code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
        // Axios client-side timeout OR gateway 504 without a response body.
        toast.error(
          L("Le modèle IA n'a pas répondu à temps (texte probablement trop volumineux). Essayez le mode « Chapitre par chapitre » ou réduisez la taille du texte.", "The AI model did not answer in time (the text is probably too large). Try « Chapter by chapter » mode or shorten the text."),
          { duration: 8000 }
        );
      } else {
        const rawMsg = err?.response?.data?.detail || err?.message || t("dashboard.summary_failed");
        const isAiLimit = /ai_daily_limit/.test(String(rawMsg));
        const msg = String(rawMsg).replace(/\s*—\s*code=ai_daily_limit/, "");
        if (mode === "chapters") {
          setChaptersError(msg);
        } else {
          // Global mode: persistent banner + clear stale progress so the
          // user never lands on the empty "En attente" state after a failure.
          setGlobalError(msg);
          setGlobalProgress(null);
        }
        if (isAiLimit) toast(msg, { duration: 12000 });
        else toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  // Global summarize via NDJSON streaming. Handles both:
  //  - Short inputs (< 80k chars): single "done" event, direct summary.
  //  - Long inputs: two-phase map/reduce flow. Phase 1 chunks the text and
  //    streams per-chunk digests (fast, ~10-20s). Phase 2 feeds the digests
  //    to the user's chosen model with their length + format, with periodic
  //    heartbeats to keep the ingress connection alive during LLM generation.
  const streamGlobalSummary = async (payload) => {
    const token = localStorage.getItem("sux1_token");
    const deviceId = (() => {
      try { return localStorage.getItem("sux1_device_id"); } catch { return null; }
    })();
    const backend = process.env.REACT_APP_BACKEND_URL;

    const consumeStream = async (path, body, onEvent) => {
      const controller = new AbortController();
      streamControllerRef.current = controller;
      // Generous per-phase timeout (180s) — production rate-limits can slow
      // Gemini map calls or Claude reduce calls; we still stay under the
      // browser's default fetch idle-timeout.
      const timeoutId = setTimeout(() => controller.abort(), 180000);
      let resp;
      try {
        resp = await fetch(`${backend}${path}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(deviceId ? { "X-Device-Id": deviceId } : {}),
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr?.name === "AbortError") {
          throw new Error(L("La requête a été interrompue (délai dépassé). Réessayez.", "The request was interrupted (timeout). Please try again."));
        }
        throw fetchErr;
      }
      if (!resp.ok) {
        clearTimeout(timeoutId);
        let detail = t("dashboard.summary_failed");
        try {
          const data = await resp.json();
          detail = data?.detail || detail;
        } catch (_) {
          /* non-JSON body */
        }
        const err = new Error(detail);
        err.response = { status: resp.status, data: { detail } };
        throw err;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let terminated = false;
      try {
        while (!terminated) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buffer.indexOf("\n")) !== -1) {
            const line = buffer.slice(0, idx).trim();
            buffer = buffer.slice(idx + 1);
            if (!line) continue;
            let obj;
            try {
              obj = JSON.parse(line);
            } catch (_) {
              continue;
            }
            const stop = onEvent(obj);
            if (stop) {
              terminated = true;
              try { await reader.cancel(); } catch (_) { /* ignore */ }
              break;
            }
          }
        }
        buffer += decoder.decode();
        if (buffer.trim()) {
          for (const line of buffer.split("\n")) {
            const s = line.trim();
            if (!s) continue;
            try { onEvent(JSON.parse(s)); } catch (_) { /* ignore */ }
          }
        }
      } finally {
        clearTimeout(timeoutId);
      }
    };

    setGlobalProgress({ phase: "starting" });

    // Phase 1: /summarize/stream → either token-streamed "done" (short input) or "map_done".
    let mapDone = null;
    let finalDone = null;
    let streamErr = null;
    let shortPartialChunks = [];
    // Live-preview throttle — stream deltas arrive dozens of times per second
    // on fast models (Gemini 3 Flash), and every setGlobalProgress call forces
    // a React re-render of the markdown-rendered preview. On mobile that cost
    // compounds and the UI thread stalls. We refresh the preview at most
    // every 120ms (≈ 8 fps) which looks smooth to the eye but roughly 10×
    // cheaper on the GC/paint pipeline.
    let lastPreviewTs = 0;
    const emitPreview = (force = false) => {
      const now = performance.now();
      if (!force && now - lastPreviewTs < 120) return;
      lastPreviewTs = now;
      setGlobalProgress({ phase: "reduce", preview: shortPartialChunks.join("") });
    };
    await consumeStream("/api/summarize/stream", payload, (obj) => {
      if (obj.type === "meta") {
        if (obj.hierarchical) {
          setGlobalProgress({ phase: "map", done: 0, total: obj.chunks });
        } else {
          setGlobalProgress({ phase: "reduce" });
        }
      } else if (obj.type === "map_progress") {
        setGlobalProgress({ phase: "map", done: obj.done, total: obj.total });
      } else if (obj.type === "reduce_delta") {
        // Short-input path streams tokens for live "typing" preview.
        // Array accumulation + throttled render keeps mobile responsive on
        // 50k+ character replies (string concatenation in a tight loop was
        // triggering GC storms on iOS Safari / Android Chrome < 110).
        shortPartialChunks.push(obj.text || "");
        emitPreview(false);
      } else if (obj.type === "map_done") {
        mapDone = obj;
        return true; // stop reading
      } else if (obj.type === "done") {
        finalDone = obj;
        return true;
      } else if (obj.type === "error") {
        streamErr = obj.detail;
        return true;
      }
      return false;
    });
    if (streamErr) throw new Error(streamErr);
    if (finalDone) {
      setGlobalProgress(null);
      setResult(finalDone);
      trackSummaryGenerated({
        model: finalDone.model,
        format: finalDone.format,
        length: finalDone.length,
        register: finalDone.register,
        source_type: finalDone.source_type,
        chars: finalDone.summary_chars,
      });
      clearHeavyInputIfNeeded();
      toast.success(t("dashboard.ready_toast"));
      notifyOperationComplete(true);
      return;
    }
    if (!mapDone) {
      // Connection was cut before "done" on the short path. Surface partial
      // content if we streamed enough to be useful.
      const shortPartial = shortPartialChunks.join("");
      if (shortPartial.trim().length > 500) {
        setGlobalProgress(null);
        setResult({
          summary: shortPartial.trim(),
          title: payload.source_label || "",
          model: payload.model,
          length: payload.length,
          format: payload.format,
          language: payload.language,
          source_type: payload.source_type,
          source_label: payload.source_label,
          original_chars: payload.text.length,
          summary_chars: shortPartial.trim().length,
          created_at: new Date().toISOString(),
          partial: true,
        });
        setGlobalError(
          L(`La connexion a été coupée avant la fin (${shortPartial.trim().length} caractères reçus). Voici le résumé partiel : cliquez sur Résumer pour réessayer.`, `The connection was cut before the end (${shortPartial.trim().length} characters received). Here is the partial summary: click Summarise to try again.`)
        );
        toast.warning(L("Résumé partiel affiché : la connexion a été coupée avant la fin.", "Partial summary shown: the connection was cut before the end."));
        return;
      }
      throw new Error(L("Réponse inattendue du serveur. Réessayez.", "Unexpected server response. Please try again."));
    }

    // Phase 2: /summarize/reduce with digests → final summary.
    setGlobalProgress({ phase: "reduce" });
    let reduceErr = null;
    let partialSummary = "";
    await consumeStream(
      "/api/summarize/reduce",
      {
        digests: mapDone.digests,
        trimmed_chars: mapDone.trimmed_chars,
        text: payload.text,
        model: payload.model,
        length: payload.length,
        format: payload.format,
        language: payload.language,
        register: payload.register,
        audience_level: payload.audience_level || null,
        source_type: payload.source_type,
        source_label: payload.source_label,
        dossier: !!payload.dossier,
      },
      (obj) => {
        if (obj.type === "reduce_delta") {
          partialSummary += obj.text || "";
          // Update progress panel with live "typing" preview so the user sees
          // words appearing instead of a static "Rédaction du résumé…" text.
          setGlobalProgress({ phase: "reduce", preview: partialSummary });
        } else if (obj.type === "done") {
          finalDone = obj;
          return true;
        } else if (obj.type === "error") {
          reduceErr = obj.detail;
          return true;
        }
        return false;
      }
    );
    if (reduceErr) throw new Error(reduceErr);
    if (!finalDone) {
      // Connection was cut before the "done" event. If we streamed enough
      // partial content to be useful (> 500 chars), surface it as a partial
      // summary rather than throwing away 45 seconds of Claude output.
      if (partialSummary.trim().length > 500) {
        setGlobalProgress(null);
        setResult({
          summary: partialSummary.trim(),
          title: payload.source_label || "",
          model: payload.model,
          length: payload.length,
          format: payload.format,
          language: payload.language,
          source_type: payload.source_type,
          source_label: payload.source_label,
          original_chars: payload.text.length,
          summary_chars: partialSummary.trim().length,
          created_at: new Date().toISOString(),
          partial: true,
        });
        setGlobalError(
          L(`Résumé produit (${partialSummary.trim().length.toLocaleString('fr-FR')} caractères). Sur les documents très longs, SUX1 limite la taille du résumé pour rester rapide ; il couvre tout le document. Cliquez sur Résumer pour en générer un nouveau si besoin.`,
            `Summary produced (${partialSummary.trim().length.toLocaleString('en-GB')} characters). On very long documents, SUX1 caps the summary size to stay fast; it covers the whole document. Click Summarise to generate a new one if needed.`)
        );
        toast.success(L("Résumé prêt (version courte pour ce très long document).", "Summary ready (short version for this very long document)."));
        notifyOperationComplete(true);
        return;
      }
      throw new Error(L("Le résumé final n'a pas été reçu. Réessayez.", "The final summary was not received. Please try again."));
    }
    setGlobalProgress(null);
    setResult(finalDone);
    trackSummaryGenerated({
      model: finalDone.model,
      format: finalDone.format,
      length: finalDone.length,
      register: finalDone.register,
      source_type: finalDone.source_type,
      chars: finalDone.summary_chars,
    });
    clearHeavyInputIfNeeded();
    toast.success(t("dashboard.ready_toast"));
    notifyOperationComplete(true);
  };

  // Reads the NDJSON stream from POST /api/summarize/chapters/stream and
  // updates state as each chapter arrives. To keep every HTTP request well
  // under the ingress 60s hard cap even when the LLM is rate-limited, the
  // work is split into small sequential batches (BATCH_SIZE chapters per
  // request). Each batch is guaranteed to complete in < ~30s and the UI
  // still shows chapters progressively across all batches.
  const streamChapters = async (payload) => {
    const BATCH_SIZE = 5;
    const token = localStorage.getItem("sux1_token");
    const backend = process.env.REACT_APP_BACKEND_URL;

    // 1) Get total chapter count via the (fast) preview endpoint so we know
    //    how many batches to run.
    let total = 0;
    try {
      const { data: prev } = await api.post("/summarize/chapters/preview", {
        text: payload.text,
      });
      total = prev?.count || 0;
    } catch (err) {
      // Preview failed → let the caller handle the error uniformly.
      throw err;
    }
    if (total <= 0) {
      const e = new Error(L("Aucune section détectée dans ce texte.", "No section detected in this text."));
      e.response = { status: 400, data: { detail: e.message } };
      throw e;
    }

    // Initialise progress panel immediately so the user sees "0 / N".
    setChaptersStream({ count: total, title: payload.source_label || "", received: [] });

    const receivedAll = [];
    let metaFinal = null;

    for (let start = 1; start <= total; start += BATCH_SIZE) {
      const end = Math.min(start + BATCH_SIZE - 1, total);
      // Per-batch timeout: abort the fetch after 90s if it hasn't finished.
      // Covers slow models (Claude Sonnet 4.5 with "Long" + "Philo" can take
      // 40s per chapter, so a 5-chapter batch may need 60-70s worst case).
      const controller = new AbortController();
      streamControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 90000);
      let resp;
      try {
        resp = await fetch(`${backend}/api/summarize/chapters/stream`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...payload, start_index: start, end_index: end }),
          signal: controller.signal,
        });
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr?.name === "AbortError") {
          throw new Error(
            receivedAll.length
              ? L(`Lot ${start}-${end} interrompu (délai dépassé) après ${receivedAll.length}/${total} chapitres. Réessayez.`, `Batch ${start}-${end} interrupted (timeout) after ${receivedAll.length}/${total} chapters. Please try again.`)
              : L("Le serveur ne répond pas. Réessayez.", "The server is not responding. Please try again.")
          );
        }
        throw fetchErr;
      }
      if (!resp.ok) {
        clearTimeout(timeoutId);
        let detail = t("dashboard.summary_failed");
        try {
          const data = await resp.json();
          detail = data?.detail || detail;
        } catch (_) {
          /* non-JSON body (HTML 502, etc.) */
        }
        const err = new Error(detail);
        err.response = { status: resp.status, data: { detail } };
        throw err;
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let doneEvent = null;
      const parseBuffer = () => {
        let idx;
        while ((idx = buffer.indexOf("\n")) !== -1) {
          const line = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 1);
          if (!line) continue;
          let obj;
          try {
            obj = JSON.parse(line);
          } catch (_) {
            continue;
          }
          if (obj.type === "meta") {
            metaFinal = obj;
          } else if (obj.type === "chapter") {
            receivedAll.push({
              index: obj.index,
              title: obj.title,
              summary: obj.summary,
              original_chars: obj.original_chars,
              summary_chars: obj.summary_chars,
            });
            const sorted = [...receivedAll].sort((a, b) => a.index - b.index);
            setChaptersStream({
              count: total,
              title: metaFinal?.title || payload.source_label || "",
              received: sorted,
            });
          } else if (obj.type === "done") {
            doneEvent = obj;
          } else if (obj.type === "refund") {
            toast(`${obj.message} (${obj.credits} crédit(s) recrédité(s))`, { duration: 12000 });
          } else if (obj.type === "error") {
            throw new Error(obj.detail || "Streaming error");
          }
          // heartbeat → ignore
        }
      };
      try {
        outer: while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          parseBuffer();
          // As soon as the backend has emitted a "done" event, we can stop
          // reading — the server has already produced every chapter for
          // this batch. Some proxies (Cloudflare HTTP/2) keep the socket
          // open past the server close, which would otherwise hang forever
          // on reader.read().
          if (doneEvent) {
            try { await reader.cancel(); } catch (_) { /* ignore */ }
            break outer;
          }
        }
        // Drain the final chunk (some servers finish the stream without a
        // trailing "\n" after the last line).
        buffer += decoder.decode();
        if (buffer.trim()) {
          buffer += "\n";
          parseBuffer();
        }
      } finally {
        clearTimeout(timeoutId);
      }
      if (!doneEvent) {
        throw new Error(
          receivedAll.length
            ? L(`Connexion interrompue après ${receivedAll.length}/${total} chapitres. Réessayez ou réduisez la taille du texte.`, `Connection interrupted after ${receivedAll.length}/${total} chapters. Try again or shorten the text.`)
            : L("Connexion interrompue avant le premier chapitre. Réessayez.", "Connection interrupted before the first chapter. Please try again.")
        );
      }
    }

    // All batches done — merge into the final chaptersResult shape so the
    // existing UI (accordion, copy-all, share) works unchanged.
    const chapters = [...receivedAll].sort((a, b) => a.index - b.index);
    const totalOriginal = chapters.reduce((s, c) => s + c.original_chars, 0);
    const totalSummary = chapters.reduce((s, c) => s + c.summary_chars, 0);
    setChaptersResult({
      id: (metaFinal && metaFinal.id) || crypto.randomUUID?.() || String(Date.now()),
      title: metaFinal?.title || payload.source_label || "",
      model: payload.model,
      length: payload.length,
      format: payload.format,
      language: payload.language,
      source_type: payload.source_type,
      source_label: payload.source_label,
      original_chars: totalOriginal,
      summary_chars: totalSummary,
      chapters,
      used_fallback: !!metaFinal?.used_fallback,
      created_at: new Date().toISOString(),
    });
    setChaptersStream(null);
    toast.success(t("dashboard.chapters_ready", { count: chapters.length }));
    notifyOperationComplete(true);
  };

  const onClear = () => {
    setDossierDocs([]);
    setText("");
    setUrl("");
    setSourceLabel(null);
    setImageFile(null);
    setResult(null);
    setChaptersResult(null);
    setChapterPreview(null);
    setChaptersStream(null);
    setChaptersError(null);
    setGlobalProgress(null);
    setGlobalError(null);
  };

  const onCopy = async () => {
    if (!result?.summary) return;
    await navigator.clipboard.writeText(result.summary);
    toast.success("Summary copied to clipboard.");
    playBell();
  };

  // "Retravailler ce résumé" — reinject the generated summary into the main
  // textarea so the user can iterate on it (produce a shorter version, change
  // format, translate as a new base, etc.). Pure client-side setText: it does
  // NOT consume a new quota slot until the user actually clicks "Résumer".
  const onReworkCurrent = () => {
    if (!result?.summary) return;
    setTab(TAB_TEXT);
    setText(result.summary);
    setReworkSource(result.title || L("résumé précédent", "previous summary"));
    setCustomInstruction("");
    setPendingFollowUp(null);
    setDirectExecution(false);
    setResult(null);
    setGlobalError(null);
    setGlobalProgress(null);
    // Scroll back to the textarea so the user sees the prefilled content.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    toast.success(L("Résumé prêt à être retravaillé.", "Summary ready to be reworked."));
  };

  // ---------------------------------------------------------------------
  // Discoverability tip for the "Question de suivi" feature.
  // Only hides when the user explicitly clicks the X — no auto-dismiss on
  // summary generation (users need to see it to know the feature exists).
  // ---------------------------------------------------------------------
  const [showFollowUpTip, setShowFollowUpTip] = useState(() => {
    try {
      if (localStorage.getItem("sux1_hide_followup_tip_v2") === "1") return false;
    } catch (_) {}
    return true;
  });
  const dismissFollowUpTip = () => {
    setShowFollowUpTip(false);
    try { localStorage.setItem("sux1_hide_followup_tip_v2", "1"); } catch (_) {}
  };

  return (
    <>
      {isLocked && (billing?.is_anonymous ? <AnonSignupModal /> : <Paywall />)}
      <BoosterPaywall
        open={boosterOpen}
        reason={boosterReason}
        onClose={() => setBoosterOpen(false)}
      />
      <PromoUpgradeModal />
      <div className="px-6 md:px-10 pt-6 border-t border-border">
        <div className="flex justify-end mb-2"><InterfaceTour /></div>
        <TimeSavedWidget />
        <LastCallBanner />
        <UsageMeter />
        {billing?.is_anonymous && !isLocked && <SummariesCounter />}
        {billing?.is_anonymous && !result && <WelcomeLine />}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12">
      {/* Input section */}
      <section className="col-span-1 lg:col-span-8 border-r border-b border-border bg-card flex flex-col min-h-[60vh]">
        <div className="grid grid-cols-2 md:grid-cols-5 border-b border-border">
          {[
            { k: TAB_TEXT, label: t("dashboard.tab_text"), icon: FileText, tid: DASH.tabText, tip: t("tabs_tip.text", "Coller un texte : collez un article, un cours, une transcription… même des centaines de pages.") },
            { k: TAB_FILE, label: t("dashboard.tab_file"), icon: Upload, tid: DASH.tabFile, tip: t("tabs_tip.file", "Téléverser un fichier : PDF, Word (DOCX), EPUB, TXT ou photo d'une page.") },
            { k: TAB_URL, label: t("dashboard.tab_url"), icon: Link2, tid: DASH.tabUrl, tip: t("tabs_tip.url", "Depuis une URL : collez l'adresse d'une page web, SUX1 en extrait le texte.") },
            { k: TAB_BOOK, label: t("tabs_tip.book_label", "Scanner un livre"), icon: Camera, tid: "tab-book", tip: t("tabs_tip.book", "Scanner un livre : photographiez la couverture, SUX1 reconnaît le livre et crée une fiche de lecture.") },
            { k: TAB_DOSSIER, label: t("dossier.tab", "Dossier"), icon: FolderOpen, tid: "tab-dossier", tip: t("dossier.tab_tip", "Dossier : rassemblez jusqu'à 5 documents (fichiers, liens, textes) pour une seule synthèse.") },
          ].map(({ k, label, icon: Icon, tid, tip }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={tid}
              data-tip={tip}
              className={`relative flex items-center justify-center gap-2 py-4 font-mono uppercase tracking-[0.14em] text-[12px] transition-colors border-r border-border last:border-r-0 ${
                tab === k
                  ? "bg-[#002FA7] text-white font-semibold"
                  : "hover:bg-secondary text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
              <span className="sm:hidden">{k === TAB_BOOK ? t("dossier.book_short", "Livre") : k === TAB_URL ? "URL" : label.split(" ")[0]}</span>
            </button>
          ))}
        </div>

        <div className="flex-1 flex flex-col">
          {tab === TAB_TEXT && !reworkSource && !text && !result && billing && !billing.is_pro && !isAdmin && (
            <InstantExample
              anonymous={!user}
              onTryReal={(t) => { setText(t); setSourceLabel(null); setImageFile(null); textAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
              onUseOwn={() => { textAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); textAreaRef.current?.focus(); }}
            />
          )}
          {tab === TAB_TEXT && user && !text && !result && <DashboardBookCard />}
          <ExtrasDrawer>
          {tab !== TAB_BOOK && <DiscoveryHeroBanner />}
          {showFollowUpTip && tab !== TAB_BOOK && (
            <div
              data-testid="dash-followup-tip"
              className="mx-6 md:mx-8 lg:mx-10 mt-6 flex items-start justify-between gap-3 border border-[#002FA7]/40 bg-[#002FA7]/5 px-4 py-3"
            >
              <div className="flex items-start gap-3 min-w-0">
                <CornerDownLeft className="w-4 h-4 text-[#002FA7] shrink-0 mt-0.5" />
                <div className="text-sm text-foreground leading-relaxed min-w-0">
                  {L(<><span className="font-semibold">Astuce.</span> Après chaque résumé, un bouton <b>« Question de suivi »</b> apparaît sous le résultat. Il permet d'enchaîner une question complémentaire (générer 5 QCM, rédiger un mail aux parents, reformuler pour un élève DYS…) <b>pour seulement 1 crédit les 5 questions</b>.</>,
                     <><span className="font-semibold">Tip.</span> After each summary, a <b>« Follow-up question »</b> button appears under the result. It lets you chain a further question (generate 5 MCQs, write an email to parents, rephrase for a DYS pupil…) <b>for just 1 credit per 5 questions</b>.</>)}
                </div>
              </div>
              <button
                type="button"
                onClick={dismissFollowUpTip}
                data-testid="dash-followup-tip-dismiss"
                aria-label={L("Fermer l'astuce", "Close tip")}
                className="text-muted-foreground hover:text-foreground shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {tab === TAB_TEXT && !reworkSource && !text && billing && !billing.is_pro && (
            <div className="mx-6 md:mx-8 lg:mx-10 mt-6">
              <PwaInstallPrompt />
              <FreeCasesStrip textAreaEmpty={!text} />
            </div>
          )}
          {tab === TAB_TEXT && !reworkSource && !text && billing && !billing.is_pro && (billing.free_summaries_used || 0) === 0 && (
            <OnboardingCTA
              onPickExample={(exampleText) => {
                setText(exampleText);
                setSourceLabel(null);
                setImageFile(null);
              }}
            />
          )}
          </ExtrasDrawer>
          {tab === TAB_TEXT && billing && !billing.is_pro && !isAdmin && credits === 0 && !isLocked && (
            <p
              data-testid="free-gemini-note"
              className="mx-6 md:mx-8 lg:mx-10 mt-4 text-[12px] text-muted-foreground text-right"
            >
              {t("billing.free_gemini_note")}
            </p>
          )}
          {tab === TAB_TEXT && billing && !billing.is_anonymous && (isPro || isAdmin || credits > 0) && (
            <div className="mx-6 md:mx-8 lg:mx-10 mt-4 flex items-center justify-end">
              <MonthlyQuotaMeter
                onOpenBooster={() => {
                  setBoosterReason("manual");
                  setBoosterOpen(true);
                }}
              />
            </div>
          )}
          {pendingFollowUp && (
            <div
              data-testid="dash-pending-followup-banner"
              className="mx-6 md:mx-8 lg:mx-10 mt-6 border-2 border-[#002FA7] bg-[#002FA7]/5 px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <CornerDownLeft className="w-4 h-4 text-[#002FA7] shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7] mb-1">
                    {L("Prompt en attente", "Prompt waiting")} · {pendingFollowUp.label}
                  </div>
                  <div className="text-sm leading-relaxed">
                    {L(<>Ajoutez votre contenu ci-dessous (coller, importer un PDF, scanner un livre…), puis lancez un résumé classique. Dès qu'il sera prêt, ce prompt s'affichera automatiquement en <b>« Question de suivi »</b> (1 crédit les 5 questions).</>,
                       <>Add your content below (paste, import a PDF, scan a book…), then run a classic summary. As soon as it is ready, this prompt will appear automatically as a <b>« Follow-up question »</b> (1 credit per 5 questions).</>)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingFollowUp(null)}
                  data-testid="dash-pending-followup-dismiss"
                  className="text-[#002FA7] hover:text-[#00227A] shrink-0"
                  title={L("Annuler le prompt", "Cancel the prompt")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {reworkSource && tab === TAB_TEXT && directExecution && (
            <div
              data-testid="dash-cas-direct-banner"
              className="mx-6 md:mx-8 lg:mx-10 mt-6 border border-[#002FA7] bg-[#002FA7]/5 px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Sparkles className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7] mb-1">
                      {L("Prompt prêt", "Prompt ready")} · {reworkSource}
                    </div>
                    <div className="text-sm leading-relaxed text-foreground mb-2">
                      {L(<>Ce prompt se suffit à lui-même : vous pouvez cliquer sur <b>RÉSUMER</b> directement, ou l'ajuster dans la zone ci-dessous avant de lancer.</>,
                         <>This prompt works on its own: you can click <b>SUMMARISE</b> directly, or adjust it in the area below before starting.</>)}
                    </div>
                    <div className="text-[12px] text-muted-foreground leading-relaxed">
                      <i>{L("SUX1 exécute l'instruction telle quelle et produit le résultat attendu (liste, QCM, mail, fiche…).", "SUX1 runs the instruction as is and produces the expected output (list, quiz, email, sheet…).")}</i>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setReworkSource(null); setDirectExecution(false); }}
                  data-testid="dash-direct-dismiss"
                  className="text-[#002FA7] hover:text-[#00227A] shrink-0"
                  title={L("Fermer l'explication", "Close the explanation")}
                  aria-label={L("Fermer l'explication", "Close the explanation")}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {reworkSource && tab === TAB_TEXT && !directExecution && customInstruction?.trim() && (
            <div
              data-testid="dash-cas-pratique-explainer"
              className="mx-6 md:mx-8 lg:mx-10 mt-6 border border-[#002FA7] bg-[#002FA7]/5 px-4 py-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <ClipboardCheck className="w-5 h-5 text-[#002FA7] shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7] mb-1">
                      {L("Prompt copié", "Prompt copied")} · {reworkSource}
                    </div>
                    <div className="text-sm leading-relaxed text-foreground mb-2">
                      {L(<>Le prompt est <b>dans votre presse-papier</b> et chargé dans <b>« Action en 1 clic »</b> ci-dessous. Pour l'exécuter dans SUX1 :</>,
                         <>The prompt is <b>in your clipboard</b> and loaded into <b>« One-click action »</b> below. To run it in SUX1:</>)}
                    </div>
                    <ol className="text-sm leading-relaxed text-foreground mb-3 pl-5 list-decimal space-y-0.5">
                      <li>{L("Collez votre contenu (texte, PDF, image…) dans la zone ci-dessous", "Paste your content (text, PDF, image…) in the area below")}</li>
                      <li>{L(<>Cliquez sur <b>RÉSUMER</b> : le prompt sera appliqué à votre contenu</>, <>Click <b>SUMMARISE</b>: the prompt will be applied to your content</>)}</li>
                    </ol>
                    <div className="text-[12px] text-muted-foreground leading-relaxed">
                      <i>{L("Vous pouvez aussi coller le prompt dans ChatGPT ou Claude avec Ctrl+V (il est déjà dans votre presse-papier).", "You can also paste the prompt into ChatGPT or Claude with Ctrl+V (it is already in your clipboard).")}</i>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="dash-cas-see-prompt"
                        onClick={() => {
                          const el = document.querySelector('[data-testid="custom-instruction-panel"]');
                          if (el) {
                            try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
                          }
                        }}
                        className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center gap-2"
                      >
                        {L("Voir le prompt chargé ↓", "See the loaded prompt ↓")}
                      </button>
                      <button
                        type="button"
                        data-testid="dash-cas-focus-textarea"
                        onClick={() => {
                          const ta = document.querySelector(`[data-testid="${DASH.textInput}"]`);
                          if (ta) {
                            try { ta.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (_) {}
                            try { ta.focus(); } catch (_) {}
                          }
                        }}
                        className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors flex items-center gap-2"
                      >
                        {L("Coller mon contenu ↓", "Paste my content ↓")}
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReworkSource(null)}
                  data-testid="dash-rework-dismiss"
                  className="text-[#002FA7] hover:text-[#00227A] shrink-0"
                  title="Fermer l'explication"
                  aria-label="Fermer l'explication"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
          {reworkSource && tab === TAB_TEXT && !customInstruction?.trim() && (
            <div
              data-testid="dash-rework-banner"
              className="mx-6 md:mx-8 lg:mx-10 mt-6 flex items-center justify-between gap-3 border border-[#002FA7]/30 bg-[#002FA7]/5 px-4 py-3"
            >
              <div className="flex items-center gap-2 min-w-0">
                <RefreshCw className="w-4 h-4 text-[#002FA7] shrink-0" />
                <span className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7] truncate">
                  {L("Retravail, basé sur :", "Rework, based on:")} <span className="normal-case tracking-normal font-semibold">{reworkSource}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setReworkSource(null)}
                data-testid="dash-rework-dismiss"
                className="text-[#002FA7] hover:text-[#00227A] shrink-0"
                title={L("Retirer l'indicateur", "Remove the indicator")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {tab === TAB_TEXT &&
            !customInstruction?.trim() &&
            !pendingFollowUp &&
            !promptWarningDismissed &&
            detectAiPrompt(text) && (
              <div
                data-testid="prompt-detected-warning"
                className="mx-6 md:mx-8 lg:mx-10 mt-6 border border-[#002FA7] bg-[#002FA7]/5 px-4 py-3 rounded-sm"
              >
                <div className="flex items-start gap-3">
                  <Sparkles className="w-4 h-4 text-[#002FA7] shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#002FA7] mb-1">
                      {L("Ce texte ressemble à une consigne IA", "This text looks like an AI instruction")}
                    </div>
                    <div className="text-sm leading-relaxed mb-3">
                      {L(<>SUX1 va <b>résumer ce texte</b> tel quel, ce qui n'a pas de sens si c'est une instruction (ex : <i>« Réécris cette consigne… »</i>). Pour l'appliquer à votre contenu, mettez-le dans <b>« Action en 1 clic »</b>.</>,
                         <>SUX1 will <b>summarise this text</b> as is, which makes no sense if it is an instruction (e.g. <i>« Rewrite this prompt… »</i>). To apply it to your content, put it in <b>« One-click action »</b>.</>)}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        data-testid="prompt-warning-shift"
                        onClick={() => {
                          setCustomInstruction(text);
                          setText("");
                          setPromptWarningDismissed(false);
                        }}
                        className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center gap-2"
                      >
                        <CornerDownLeft className="w-3.5 h-3.5" />
                        {L("Mettre dans Action en 1 clic", "Move to One-click action")}
                      </button>
                      <button
                        type="button"
                        data-testid="prompt-warning-dismiss"
                        onClick={() => setPromptWarningDismissed(true)}
                        className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-border hover:bg-foreground/10 transition-colors"
                      >
                        {L("Ignorer, résumer quand même", "Ignore, summarise anyway")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          {tab === TAB_TEXT && (
            <textarea
              ref={textAreaRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                autoGrow(e.target);
                setSourceLabel(null);
                setImageFile(null);
                // Reset the "ignore" flag when the field becomes empty so
                // the warning can fire again on the next paste of a prompt.
                if (!e.target.value.trim()) setPromptWarningDismissed(false);
              }}
              onPaste={(e) => {
                // Defensive sync for mobile browsers where the native
                // "Paste" context-menu action may not always trigger a
                // React `input` event reliably. We read the clipboard
                // payload, compose the new value at the current caret,
                // and manually push it to state so the DOM and React
                // never drift apart — which prevents the "I can no
                // longer type after pasting" ghost-caret bug.
                const clip = e.clipboardData;
                if (!clip) return;
                const pasted = clip.getData("text");
                if (!pasted) return;
                const el = e.currentTarget;
                const start = el.selectionStart ?? el.value.length;
                const end = el.selectionEnd ?? el.value.length;
                const next = el.value.slice(0, start) + pasted + el.value.slice(end);
                e.preventDefault();
                setText(next);
                setSourceLabel(null);
                setImageFile(null);
                // Restore caret at the end of the pasted chunk on the next
                // frame (after React has re-rendered with the new value).
                const caret = start + pasted.length;
                requestAnimationFrame(() => {
                  try {
                    el.focus();
                    el.setSelectionRange(caret, caret);
                    autoGrow(el);
                  } catch { /* focus lost, harmless */ }
                });
              }}
              data-testid={DASH.textInput}
              placeholder={
                format === "biographie"
                  ? (
                      ficheType === "lieu"
                        ? L("Tapez le nom d'un lieu (ex : Mont-Saint-Michel, Kyoto, Venise). Ajoutez un contexte si besoin : pays, période, type (ville, monument, région).", "Type the name of a place (e.g. Mont-Saint-Michel, Kyoto, Venice). Add context if needed: country, period, type (city, monument, region).")
                        : ficheType === "oeuvre"
                        ? L("Tapez le titre d'une œuvre (ex : Guernica, Les Misérables, La Joconde). Précisez l'auteur si le titre est ambigu.", "Type the title of a work (e.g. Guernica, Les Misérables, the Mona Lisa). Add the author if the title is ambiguous.")
                        : ficheType === "concept"
                        ? L("Tapez le nom d'un concept ou d'une notion (ex : la photosynthèse, la relativité, le structuralisme). Ajoutez un domaine si besoin.", "Type the name of a concept or notion (e.g. photosynthesis, relativity, structuralism). Add a field if needed.")
                        : ficheType === "evenement"
                        ? L("Tapez le nom d'un événement (ex : la Révolution française, la chute du mur de Berlin, la découverte de la pénicilline).", "Type the name of an event (e.g. the French Revolution, the fall of the Berlin Wall, the discovery of penicillin).")
                        : ficheType === "auto"
                        ? L("Tapez le nom d'une personne, lieu, œuvre, concept ou événement : SUX1 détecte automatiquement le type.", "Type the name of a person, place, work, concept or event: SUX1 detects the type automatically.")
                        : L("Tapez le nom d'une personne (ex : Victor Hugo, Marie Curie, Nelson Mandela). Ajoutez un contexte si besoin : siècle, nationalité, domaine.", "Type the name of a person (e.g. Victor Hugo, Marie Curie, Nelson Mandela). Add context if needed: century, nationality, field.")
                    )
                  : format === "metaphore" || format === "poeme"
                  ? t(`metaphor_hint.${format === "poeme" ? "poem_placeholder" : "placeholder"}`)
                  : t("dashboard.paste_placeholder")
              }
              className="block w-full p-6 md:p-8 lg:p-10 pb-40 font-['IBM_Plex_Sans'] text-base md:text-[17px] leading-relaxed bg-transparent border-0 focus:outline-none resize-none min-h-[260px] md:min-h-[420px] max-h-[1800px] cursor-text"
            />
          )}

          {tab === TAB_TEXT && ["metaphore", "poeme"].includes(format) && !reworkSource && (
            <MetaphorHint poem={format === "poeme"} onPick={(ex) => { setText(ex); setSourceLabel(null); setImageFile(null); }} />
          )}

          {tab === TAB_FILE && (
            <div className="flex-1 p-8 lg:p-10 flex flex-col">
              <div
                className="border border-dashed border-border p-12 flex flex-col items-center justify-center gap-4 hover:bg-secondary/40 transition-colors cursor-pointer"
                onClick={() => fileRef.current?.click()}
              >
                <Upload className="w-8 h-8 text-[#002FA7]" />
                <div className="font-mono uppercase tracking-[0.2em] text-xs">
                  {t("dashboard.file_types")}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("dashboard.file_help")}
                </p>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.epub,.txt,.md,.jpg,.jpeg,.png,.webp,.heic,.heif,image/*"
                  onChange={onPickFile}
                  data-testid={DASH.fileInput}
                  className="hidden"
                />
                <button
                  data-testid={DASH.fileExtractBtn}
                  disabled={extracting}
                  className="mt-2 bg-[#002FA7] text-white font-mono uppercase tracking-[0.18em] text-[12px] px-8 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 shadow-sm"
                >
                  {extracting ? t("dashboard.extracting") : t("dashboard.choose_file")}
                </button>
              </div>

              {text && (
                <div className="mt-6 flex items-start gap-4">
                  {imageFile && (
                    <div className="pt-[38px]">
                      <ImagePreview file={imageFile} testidPrefix="dashboard-image-preview" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="[&>div]:mt-0">
                      <ExtractedPreview text={text} sourceLabel={sourceLabel || "uploaded file"} previewLimit={1500} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === TAB_URL && (
            <div className="flex-1 p-8 lg:p-10 flex flex-col">
              <label className="sux-marker mb-2">{t("dashboard.url_label")}</label>
              <div className="flex gap-0 border border-border">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={t("dashboard.url_placeholder")}
                  data-testid={DASH.urlInput}
                  className="flex-1 px-4 py-3 font-mono text-sm bg-transparent focus:outline-none"
                />
                <button
                  onClick={onExtractUrl}
                  disabled={extracting || !url.trim()}
                  data-testid={DASH.urlExtractBtn}
                  className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.14em] text-[12px] px-6 hover:bg-[#001E6B] transition-colors disabled:opacity-50"
                >
                  {extracting ? t("dashboard.fetching") : t("dashboard.fetch")}
                </button>
              </div>

              {text && <ExtractedPreview text={text} sourceLabel={sourceLabel || url} previewLimit={1500} />}
            </div>
          )}

          {tab === TAB_DOSSIER && (
            <DossierPanel docs={dossierDocs} setDocs={setDossierDocs} disabled={busy} />
          )}
          {tab === TAB_BOOK && (
            <BookScanPanel model={model} language={language} register={register} audienceLevel={audienceLevel} />
          )}
        </div>

        {tab !== TAB_BOOK && (
        <div className="px-6 md:px-8 py-3 border-t border-border flex items-center justify-between bg-secondary/40">
          <div className="sux-marker" data-testid={DASH.charCount} data-tip={L("Compteur : caractères et mots. 1 crédit couvre environ 20 pages (60 000 caractères).", "Counter: characters and words. 1 credit covers about 20 pages (60,000 characters).")}>
            {charCount.toLocaleString()} {t("dashboard.chars")} · {wordCount.toLocaleString()} {t("dashboard.words")}
          </div>
          <button
            onClick={onClear}
            data-testid={DASH.clearBtn}
            data-tip={L("Effacer : vide la zone de texte pour repartir de zéro.", "Clear: empties the text area to start again.")}
            className="font-mono uppercase tracking-[0.14em] text-[11px] flex items-center gap-2 text-muted-foreground hover:text-[#002FA7] transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> {t("dashboard.clear")}
          </button>
        </div>
        )}
        {tab !== TAB_BOOK && (
          <BigBookTip
            chars={charCount}
            mode={mode}
            model={model}
            length={length}
            format={format}
            onApply={() => {
              if (canChapters) setMode("chapters");
              setModel("gemini-3-flash");
              setLength("medium");
              setFormat("both");
              toast.success(canChapters ? t("trial.bigbook_toast") : t("trial.bigbook_toast_locked"));
            }}
          />
        )}
        {tab !== TAB_BOOK && <CreditEstimate chars={charCount} mode={mode} model={model} chapterCredits={chapterPreview?.credits} />}
        <CustomInstructionPanel
          value={customInstruction}
          onChange={setCustomInstruction}
          expandOnMount={Boolean(customInstruction)}
          disabled={busy}
        />
      </section>

      {/* Controls section — extracted to <OutputConfigPanel/> */}
      <OutputConfigPanel
        tab={tab}
        TAB_BOOK={TAB_BOOK}
        model={model}
        setModel={setModel}
        length={length}
        setLength={setLength}
        format={format}
        setFormat={setFormat}
        formatDisabled={Boolean(customInstruction?.trim())}
        audienceLevel={audienceLevel}
        setAudienceLevel={setAudienceLevel}
        ficheType={ficheType}
        setFicheType={setFicheType}
        language={language}
        setLanguage={setLanguage}
        register={register}
        setRegister={setRegister}
        mode={mode}
        setMode={setMode}
        meta={meta}
        disabledModels={disabledModels}
        modelNote={modelNote}
        isPro={canChapters}
        chapterPreview={chapterPreview}
        onSummarize={onSummarize}
        busy={busy}
        text={text}
        online={online}
        simpleDefault={Boolean(billing?.is_anonymous)}
      />

      {/* Mobile sticky Summarize CTA — always accessible on small screens (not in book mode).
          Hidden when the paywall is active since the user cannot run a new summary anyway
          (both the Pro paywall and the anon signup paywall replace the Dashboard content). */}
      {tab !== TAB_BOOK && !isLocked && (
      <div className="lg:hidden fixed bottom-16 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur-md p-3">
        <button
          onClick={onSummarize}
          disabled={busy || text.trim().length < 3 || !online}
          data-testid="summarize-btn-mobile"
          className="w-full bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-xs py-4 hover:bg-[#00227A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md"
          title={!online ? t("offline.button_disabled") : undefined}
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> {t("dashboard.summarizing")}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> {t("dashboard.summarize")}
              {text.trim().length >= 10 && (
                <span className="opacity-70 normal-case tracking-normal">· {wordCount.toLocaleString()} {t("dashboard.words")}</span>
              )}
            </>
          )}
        </button>
      </div>
      )}

      {/* Result section (hidden in Book scan mode — BookScanPanel has its own) */}
      {tab !== TAB_BOOK && (
      <section className="col-span-1 lg:col-span-12 border-b border-border bg-card pb-40 lg:pb-0">
        <div className="p-10 lg:p-12">
          <div className="flex items-start justify-between mb-12 lg:mb-10 flex-wrap gap-4">
            <div>
              <div className="sux-marker mb-2">{t("dashboard.result_marker")}</div>
              <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                {result ? result.title : chaptersResult ? chaptersResult.title : t("dashboard.awaiting")}
              </h2>
              {result && (() => {
                const rawRatio = Math.round((1 - result.summary_chars / Math.max(result.original_chars, 1)) * 100);
                const compressionOk = result.original_chars >= 100 && rawRatio > 0;
                return (
                <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-[0.12em] text-muted-foreground">
                  <span className="border border-border bg-secondary/50 px-2.5 py-1">{result.model}</span>
                  <span className="border border-border bg-secondary/50 px-2.5 py-1">{result.length}</span>
                  <span className="border border-border bg-secondary/50 px-2.5 py-1">{result.format}</span>
                  <span className="border border-border bg-secondary/50 px-2.5 py-1">
                    {result.original_chars.toLocaleString()} → {result.summary_chars.toLocaleString()} chars
                  </span>
                  {compressionOk && (
                    <span className="border border-[#002FA7] text-[#002FA7] px-2.5 py-1 font-semibold">
                      {rawRatio}% {t("dashboard.shorter")}
                    </span>
                  )}
                </div>
                );
              })()}
              {chaptersResult && (
                <div className="mt-3 flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  <span className="border border-[#002FA7] text-[#002FA7] px-2 py-1 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" />
                    {t("dashboard.chapters_ready", { count: chaptersResult.chapters.length })}
                  </span>
                  <span className="border border-border px-2 py-1">{chaptersResult.model}</span>
                  <span className="border border-border px-2 py-1">{chaptersResult.length}</span>
                  <span className="border border-border px-2 py-1">
                    {t("dashboard.chapters_total_compression", {
                      from: chaptersResult.original_chars.toLocaleString(),
                      to: chaptersResult.summary_chars.toLocaleString(),
                    })}
                  </span>
                  <span className="border border-[#002FA7] text-[#002FA7] px-2 py-1">
                    {Math.round((1 - chaptersResult.summary_chars / Math.max(chaptersResult.original_chars, 1)) * 100)}% {t("dashboard.shorter")}
                  </span>
                </div>
              )}
            </div>
            {result && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={onCopy}
                  data-testid={DASH.copyBtn}
                  data-tip={L("Copier : copie le résumé pour le coller dans un document, un mail ou l'ENT.", "Copy: copies the summary to paste into a document, an email or your school platform.")}
                  className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2"
                >
                  <Copy className="w-3.5 h-3.5" /> {t("common.copy") || "Copy"}
                </button>
                <ShareAndPrint
                  title={result.title}
                  text={result.summary}
                  isPublic={!!result.is_public}
                  publicUrl={resultShareUrl}
                  onEnsurePublic={result.is_anonymous ? null : onEnsureResultPublic}
                  needsAccount={!!result.is_anonymous}
                  sharing={sharingResult}
                  testidPrefix="dashboard"
                />
              </div>
            )}
          </div>

          <div data-testid={DASH.resultArea}>
            {!result && !chaptersResult && !chaptersStream && !chaptersError && !globalError && !globalProgress && !busy && (
              <div className="border border-dashed border-border p-10 md:p-14 text-center bg-secondary/30">
                <Sparkles className="w-8 h-8 text-[#002FA7] mx-auto mb-4 opacity-70" />
                <div className="sux-marker mb-3 text-foreground">{t("dashboard.no_summary_yet")}</div>
                <p className="text-base text-muted-foreground max-w-md mx-auto leading-relaxed">
                  {t("dashboard.no_summary_help")}
                </p>
              </div>
            )}

            {chaptersError && (
              <div
                data-testid="chapters-error-banner"
                className="border border-[#002FA7] bg-[#002FA7]/5 p-5 mb-6"
              >
                <div className="sux-marker mb-2 text-[#002FA7]">
                  {L("Le résumé par chapitre a échoué", "The chapter summary failed")}
                </div>
                <p className="text-sm text-foreground leading-relaxed mb-3">
                  {chaptersError}
                </p>
                <button
                  onClick={() => setChaptersError(null)}
                  className="font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                  data-testid="chapters-error-dismiss"
                >
                  {L("Fermer", "Close")}
                </button>
              </div>
            )}

            {globalError && (
              <div
                data-testid="global-error-banner"
                className="border border-[#002FA7] bg-[#002FA7]/5 p-5 mb-6"
              >
                <div className="sux-marker mb-2 text-[#002FA7]">
                  {L("Le résumé global a échoué", "The global summary failed")}
                </div>
                <p className="text-sm text-foreground leading-relaxed mb-3">
                  {globalError}
                </p>
                <p className="text-xs text-muted-foreground mb-3">
                  {L("Astuce : essayez le mode « Chapitre par chapitre », plus fiable sur les très longs textes, ou choisissez Gemini 3 Flash (plus rapide) au lieu de Claude Sonnet 4.5.", "Tip: try « Chapter by chapter » mode, more reliable on very long texts, or choose Gemini 3 Flash (faster) instead of Claude Sonnet 4.5.")}
                </p>
                <button
                  onClick={() => setGlobalError(null)}
                  className="font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                  data-testid="global-error-dismiss"
                >
                  {L("Fermer", "Close")}
                </button>
              </div>
            )}

            {busy && !chaptersStream && (
              <div className="border border-border p-6 md:p-8 text-muted-foreground">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <Loader2 className="w-5 h-5 animate-spin text-[#002FA7]" />
                  <span className="font-mono uppercase tracking-[0.2em] text-[11px]">
                    {mode === "chapters" && chapterPreview
                      ? t("dashboard.chapters_summarizing", { count: chapterPreview.count })
                      : globalProgress?.phase === "map"
                      ? L(`Analyse du texte… ${globalProgress.done || 0} / ${globalProgress.total || "?"} sections`, `Analysing the text… ${globalProgress.done || 0} / ${globalProgress.total || "?"} sections`)
                      : globalProgress?.phase === "reduce"
                      ? L("Rédaction du résumé final…", "Writing the final summary…")
                      : t("dashboard.distilling")}
                  </span>
                </div>
                {globalProgress?.phase === "map" && globalProgress.total > 0 && (
                  <div className="w-64 h-1.5 bg-border overflow-hidden mx-auto">
                    <div
                      className="h-full bg-[#002FA7] transition-all duration-300"
                      style={{
                        width: `${Math.round(((globalProgress.done || 0) / globalProgress.total) * 100)}%`,
                      }}
                    />
                  </div>
                )}
                {globalProgress?.phase === "reduce" && globalProgress.preview && (
                  <div
                    className="mt-2 max-w-3xl mx-auto bg-secondary/30 border border-border p-4 text-sm text-foreground leading-relaxed whitespace-pre-wrap"
                    data-testid="global-reduce-preview"
                  >
                    {globalProgress.preview}
                    <span className="inline-block w-2 h-4 bg-[#002FA7] animate-pulse ml-1 align-middle" />
                  </div>
                )}
              </div>
            )}

            {chaptersStream && !chaptersResult && (
              <div data-testid="chapters-stream-progress" className="max-w-4xl">
                <div className="border border-border bg-secondary/30 px-5 py-4 mb-4 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                    <span className="sux-marker">
                      {chaptersStream.received.length} / {chaptersStream.count} {L("chapitres résumés…", "chapters summarised…")}
                    </span>
                  </div>
                  <div className="w-full sm:w-64 h-1.5 bg-border overflow-hidden">
                    <div
                      className="h-full bg-[#002FA7] transition-all duration-300"
                      style={{
                        width: `${
                          chaptersStream.count > 0
                            ? Math.round((chaptersStream.received.length / chaptersStream.count) * 100)
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
                {chaptersStream.received.length > 0 && (
                  <Accordion
                    type="multiple"
                    defaultValue={chaptersStream.received.map((c) => `chap-${c.index}`)}
                    className="border border-border bg-card"
                  >
                    {chaptersStream.received.map((c) => (
                      <AccordionItem
                        key={c.index}
                        value={`chap-${c.index}`}
                        className="border-b border-border last:border-b-0"
                      >
                        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-secondary/40">
                          <div className="flex items-center gap-4 flex-1 min-w-0 text-left">
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7] border border-[#002FA7] px-2 py-1 shrink-0">
                              {t("dashboard.chapter_index", { index: String(c.index).padStart(2, "0") })}
                            </span>
                            <span
                              className="text-base font-semibold truncate"
                              style={{ fontFamily: "Outfit" }}
                              translate="no"
                            >
                              {c.title}
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-5 pb-6 pt-2">
                          <article
                            className="summary-content notranslate"
                            translate="no"
                            dangerouslySetInnerHTML={{ __html: renderSummary(c.summary) }}
                          />
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            )}

            {result && (
                <ErrorBoundary
                  fallback={(msg, reset) => (
                    <div className="border border-[#002FA7] bg-[#002FA7]/5 p-6 text-sm">
                      <div className="sux-marker mb-2 text-[#002FA7]">
                        {t("dashboard.render_failed_title")}
                      </div>
                      <p className="text-sm mb-3">
                        {t("dashboard.render_failed_help")}
                      </p>
                      <pre className="font-mono text-[11px] bg-card p-3 border border-border max-h-40 overflow-auto whitespace-pre-wrap" translate="no">
                        {String(result.summary || "")}
                      </pre>
                      <button
                        onClick={reset}
                        className="mt-3 font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition-colors"
                      >
                        {t("common.try_again")}
                      </button>
                    </div>
                  )}
                >
                  <div className="space-y-14 lg:space-y-12">
                  <div className="max-w-3xl flex items-center justify-between flex-wrap gap-3">
                    <div className="sux-marker">{t("dashboard.summary_label", { defaultValue: "Résumé" })}</div>
                    <SpeakButton
                      text={result.summary}
                      lang={language !== "auto" ? language : undefined}
                      testId="global-speak-btn"
                    />
                  </div>
                  <VerifiedSummary
                    className="summary-content max-w-3xl notranslate"
                    summary={result.summary}
                    source={result.original_text || text}
                    format={result.format}
                    testid="dash-result-summary"
                  />
                  {result.format === "poeme" && (
                    <ReciteBox summary={result.summary} lang={language !== "auto" ? language : undefined} />
                  )}
                  <PrintQr url={result.is_public ? resultShareUrl : null} />
                  {(() => {
                    const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;
                    const orig = wc(text);
                    const sum = wc(result.summary);
                    if (!orig || !sum) return null;
                    // Keep two decimals so 99.83% never rounds up to a
                    // misleading "100%" on very long inputs (e.g. Les
                    // Misérables: 568 652 → 943 words). Cap at 99.99% so
                    // we never claim a full 100% shrinkage.
                    const rawPct = Math.max(0, ((orig - sum) / orig) * 100);
                    const saved = rawPct >= 99.99 ? "99,99" : rawPct.toFixed(2).replace(".", ",");
                    return (
                      <div
                        data-testid="word-count-caption"
                        className="max-w-3xl font-mono uppercase tracking-[0.15em] text-[10px] text-muted-foreground border-t border-border pt-3"
                      >
                        {orig.toLocaleString(i18n.language)} {t("dashboard.words")} &rarr; {sum.toLocaleString(i18n.language)} {t("dashboard.words")}
                        {rawPct > 0 && (
                          <>
                            {" · "}
                            <span className="text-[#002FA7]">{t("dashboard.saved")} : {saved}%</span>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  <SummaryActions
                    summary={result.summary}
                    model={model}
                    onReplace={(newText) => setResult({ ...result, summary: newText, summary_chars: newText.length })}
                  />
                  {/* Retravailler ce résumé — reinject into the main textarea
                      so the user can iterate (new length, new format, new
                      language, polished further…). No quota is spent until
                      the user explicitly runs the next summary. */}
                  <div className="max-w-3xl mt-8">
                    <ActionBarButton
                      icon={RefreshCw}
                      label={t("rework.label", "Retravailler ce résumé")}
                      onClick={onReworkCurrent}
                      testid="dash-rework-current-btn"
                      help={ACTION_HELP.rework}
                      chevron={false}
                      tip={t("rework.tip", "Retravailler : recolle ce résumé dans l'éditeur comme nouveau texte source, pour le transformer à nouveau (autre longueur, format, QCM, DYS, traduction…).")}
                    />
                    <p className="mt-2 text-center text-[11px] text-muted-foreground">
                      {t("rework.caption", "Repartir de ce résumé comme nouveau texte source.")}
                    </p>
                  </div>
                  <div className="max-w-3xl grid grid-cols-1 sm:grid-cols-2 gap-4 items-start" data-testid="dash-followup-flashcards-row">
                  <FollowUpBox
                    summary={result.summary}
                    model={model}
                    initialQuestion={pendingFollowUp?.prompt}
                    initialQuestionLabel={pendingFollowUp?.label}
                    onPrefillConsumed={() => setPendingFollowUp(null)}
                    summaryId={result.is_anonymous ? null : result.id}
                    title={result.title}
                    onAppended={(s) => setResult((r) => ({ ...r, summary: s, summary_chars: s.length }))}
                  />
                  <FlashcardsButton summaryId={result.id} />
                  <MindmapButton summaryId={result.id} />
                  </div>
                  {!result.is_anonymous && (
                    <TripleLevels source={result.original_text} fallback={result.summary}
                      config={{ length: result.length, format: result.format, language: result.language || "auto", register: result.register || "auto", source_type: "text", title: result.title, custom_instruction: customInstruction?.trim() || null, fiche_type: result.format === "biographie" ? ficheType : null }} />
                  )}
                  <ReviewPrompt summaryId={result.id} />
                  <BookPromoBanner />
                  </div>
                </ErrorBoundary>
            )}

            {chaptersResult && !busy && (
              <div data-testid={DASH.chaptersResult} className="max-w-4xl">
                <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
                  <div className="sux-marker">
                    {t("dashboard.chapters_hint_expand", { count: chaptersResult.chapters.length })}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        const all = chaptersResult.chapters
                          .map((c) => {
                            const summary = (c.summary || "").trimStart();
                            return summary.startsWith("## ")
                              ? summary
                              : `## ${c.title}\n\n${summary}`;
                          })
                          .join("\n\n---\n\n");
                        navigator.clipboard.writeText(all).then(
                          () => toast.success(t("dashboard.chapters_copied_all")),
                          () => toast.error(t("dashboard.copy_failed"))
                        );
                      }}
                      className="font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-border hover:bg-[#002FA7] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2"
                    >
                      <Copy className="w-3.5 h-3.5" /> {t("dashboard.copy_all")}
                    </button>
                    <ShareAndPrint
                      title={chaptersResult.title || "SUX1 — Résumé par chapitre"}
                      text={chaptersResult.chapters
                        .map((c) => {
                          const summary = (c.summary || "").trimStart();
                          return summary.startsWith("## ")
                            ? summary
                            : `## ${c.title}\n\n${summary}`;
                        })
                        .join("\n\n---\n\n")}
                      url={null}
                      testidPrefix="chapters"
                    />
                  </div>
                </div>
                <Accordion
                  type="multiple"
                  defaultValue={chaptersResult.chapters.map((c) => `chap-${c.index}`)}
                  className="border border-border bg-card"
                >
                  {chaptersResult.chapters.map((c) => (
                    <AccordionItem
                      key={c.index}
                      value={`chap-${c.index}`}
                      data-testid={DASH.chapterItem(c.index)}
                      className="border-b border-border last:border-b-0"
                    >
                      <AccordionTrigger
                        data-testid={DASH.chapterTrigger(c.index)}
                        className="px-5 py-4 hover:no-underline hover:bg-secondary/40 group"
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0 text-left">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7] border border-[#002FA7] px-2 py-1 shrink-0">
                            {t("dashboard.chapter_index", { index: String(c.index).padStart(2, "0") })}
                          </span>
                          <span
                            className="text-base font-semibold truncate"
                            style={{ fontFamily: "Outfit" }}
                            translate="no"
                          >
                            {c.title}
                          </span>
                          <span className="ml-auto mr-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hidden sm:inline shrink-0">
                            {t("dashboard.chapter_chars", {
                              from: c.original_chars.toLocaleString(),
                              to: c.summary_chars.toLocaleString(),
                            })}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-6 pt-2">
                        <ErrorBoundary
                          fallback={(msg, reset) => (
                            <div className="border border-[#002FA7] bg-[#002FA7]/5 p-4 text-sm">
                              <pre className="font-mono text-[11px] whitespace-pre-wrap" translate="no">
                                {String(c.summary || "")}
                              </pre>
                              <button
                                onClick={reset}
                                className="mt-2 font-mono uppercase tracking-[0.2em] text-[11px] px-3 py-2 border border-[#002FA7] text-[#002FA7]"
                              >
                                {t("common.try_again")}
                              </button>
                            </div>
                          )}
                        >
                          <article
                            className="summary-content notranslate"
                            translate="no"
                            dangerouslySetInnerHTML={{ __html: renderSummary(c.summary) }}
                          />
                        </ErrorBoundary>
                        <div className="mt-4 flex flex-wrap items-center gap-2">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(c.summary);
                              toast.success(t("common.copied"));
                            }}
                            className="font-mono uppercase tracking-[0.2em] text-[10px] px-3 py-2 border border-border hover:bg-[#002FA7] hover:text-white transition-colors inline-flex items-center gap-2"
                          >
                            <Copy className="w-3 h-3" /> {t("dashboard.copy_chapter")}
                          </button>
                          <SpeakButton
                            text={c.summary}
                            lang={language !== "auto" ? language : undefined}
                            compact
                            testId={`chapter-speak-btn-${c.index}`}
                          />
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <BookPromoBanner />
              </div>
            )}
          </div>
        </div>
      </section>
      )}
      </div>
    </>
  );
}
